import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  constructor() { }

  /**
   * Convierte una llave pública en formato PEM a CryptoKey
   * @param publicKeyPem Llave pública en formato PEM
   * @returns Promise<CryptoKey>
   */
  private async importPublicKey(publicKeyPem: string): Promise<CryptoKey> {
    // Remover headers y footers del PEM y espacios en blanco
    const pemContents = publicKeyPem
      .replace(/-----BEGIN PUBLIC KEY-----/, '')
      .replace(/-----END PUBLIC KEY-----/, '')
      .replace(/\s/g, '');

    // Convertir de base64 a ArrayBuffer
    const binaryDer = this.base64ToArrayBuffer(pemContents);

    // Importar la llave
    return await window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      false,
      ['encrypt']
    );
  }

  /**
   * Convierte string base64 a ArrayBuffer
   * @param base64 String en base64
   * @returns ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Convierte ArrayBuffer a string base64
   * @param buffer ArrayBuffer
   * @returns String en base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Cifra un texto usando RSA-OAEP-256
   * @param plaintext Texto plano a cifrar
   * @param publicKeyPem Llave pública en formato PEM
   * @returns Promise<string> Texto cifrado en base64
   */
  async encryptWithRSA(plaintext: string, publicKeyPem: string): Promise<string> {
    try {
      // Importar la llave pública
      const publicKey = await this.importPublicKey(publicKeyPem);

      // Convertir el texto a ArrayBuffer
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);

      // Cifrar los datos
      const encryptedData = await window.crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP'
        },
        publicKey,
        data
      );

      // Convertir a base64 para envío
      return this.arrayBufferToBase64(encryptedData);
    } catch (error) {
      console.error('Error al cifrar con RSA-OAEP:', error);
      throw new Error('Error al cifrar los datos');
    }
  }

  /**
   * Valida si una llave pública PEM es válida
   * @param publicKeyPem Llave pública en formato PEM
   * @returns boolean
   */
  isValidPublicKey(publicKeyPem: string): boolean {
    try {
      if (!publicKeyPem || typeof publicKeyPem !== 'string') {
        return false;
      }

      // Verificar que tenga el formato PEM básico
      const pemPattern = /^-----BEGIN PUBLIC KEY-----[\s\S]*-----END PUBLIC KEY-----$/;
      return pemPattern.test(publicKeyPem.trim());
    } catch (error) {
      return false;
    }
  }
}
